import React, { useRef, useState, useEffect } from "react";
import { GoArrowRight } from "react-icons/go";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import langData from "../locales/langData.json";
import { Link } from "react-router-dom";
import "../assets/css/VideoSlider.css";

const VideoSlider = ({ language = "en" }) => {
  const slides = langData[language].home.videoSlider.slides;
  const seeMore = langData[language].home.videoSlider.seeMore;
  const viewAll = langData[language].home.videoSlider.viewAll;
  const viewLink = langData[language].home.videoSlider.viewlink || "/products";

  const isRTL = language === "ar"; // detect Arabic / RTL mode
  const sliderRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isGifActive, setIsGifActive] = useState([]);

  // Initialize gif active state
  useEffect(() => {
    setIsGifActive(slides.map((_, i) => i === 0));
  }, [slides]);

  // Scroll to specific index (RTL-aware)
  const scrollToIndex = (index) => {
    if (!sliderRef.current) return;
    const slider = sliderRef.current;
    const slideWidth = slider.offsetWidth * 0.8;
    setIsAnimating(true);

    const scrollPos = isRTL
      ? slider.scrollWidth - slideWidth * (index + 1)
      : slideWidth * index;

    slider.scrollTo({
      left: scrollPos,
      behavior: "smooth",
    });

    setTimeout(() => {
      setActiveSlide(index);
      setIsAnimating(false);
    }, 800);
  };

  const scrollSlide = (direction) => {
    if (isAnimating) return;
    let newIndex = activeSlide + direction;
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= slides.length) newIndex = slides.length - 1;
    scrollToIndex(newIndex);
  };

  // Detect active slide during manual scroll (RTL-aware)
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      const slideWidth = slider.offsetWidth * 0.8;
      let newIndex;

      if (isRTL) {
        const maxScroll = slider.scrollWidth - slider.clientWidth;
        const scrolled = maxScroll - slider.scrollLeft;
        newIndex = Math.round(scrolled / slideWidth);
      } else {
        newIndex = Math.round(slider.scrollLeft / slideWidth);
      }

      if (newIndex !== activeSlide) {
        setActiveSlide(newIndex);
        setIsGifActive(slides.map((_, i) => i === newIndex));
      }
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, [activeSlide, slides, isRTL]);

  return (
    <div
      className={`container my-3 position-relative ${isRTL ? "rtl-mode" : ""}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Arrows */}
      {!isRTL ? (
        <>
          {activeSlide > 0 && (
            <button
              className="slider-arrow left"
              onClick={() => scrollSlide(-1)}
              disabled={activeSlide === 0}
            >
              <FiChevronLeft size={30}  className="slider-arro"/>
            </button>
          )}

          {activeSlide < slides.length - 1 && (
            <button
              className="slider-arrow right"
              onClick={() => scrollSlide(1)}
              disabled={activeSlide === slides.length - 1}
            >
              <FiChevronRight size={30} className="slider-arro"/>
            </button>
          )}
        </>
      ) : (
        <>
          {activeSlide > 0 && (
            <button
              className="slider-arrow right"
              onClick={() => scrollSlide(-1)}
              disabled={activeSlide === 0}
            >
              <FiChevronRight size={30}  className="slide-arr"/>
            </button>
          )}
          {activeSlide < slides.length - 1 && (
            <button
              className="slider-arrow left"
              onClick={() => scrollSlide(1)}
              disabled={activeSlide === slides.length - 1}
            >
              <FiChevronLeft size={30} className="slide-arr" />
            </button>
          )}
        </>
      )}

      {/* Slides */}
      <div className="slider-card" ref={sliderRef}>
        {slides.map((slide, i) => (
          <div
            className={`slide-card ${activeSlide === i ? "active" : ""}`}
            key={i}
          >
            <div className="video-con">
              <img
                src={slide.thumb}
                alt={slide.title}
                className={`thumb-img ${isGifActive[i] ? "hidden" : ""}`}
                loading="lazy"
              />
              <img
                src={slide.gif}
                alt={slide.title}
                className={`gif-img ${isGifActive[i] ? "active" : ""}`}
                loading="lazy"
              />
            </div>

            <div
              className={`slider-con ${
                window.innerWidth <= 768 && activeSlide !== i
                  ? "hide-on-mobile"
                  : ""
              }`}
            >
              <h2>{slide.title}</h2>
              <p>{slide.desc}</p>
              <Link to={slide.link} className="mob-seemore">
                <button className="explore-btn space-btns mob-view-btn">
                  {seeMore}{" "}
                  {isRTL ? (
                    <GoArrowRight style={{ transform: "rotate(180deg)" }} />
                  ) : (
                    <GoArrowRight />
                  )}
                </button>
              </Link>
            </div>
          </div>
        ))}
      </div>

      {/* View All */}
      <Link to={viewLink}>
        <div className={`view-all ${isRTL ? "view-all-left" : "view-all-right"}`}>
          {viewAll}{" "}
          {isRTL ? (
            <GoArrowRight style={{ transform: "rotate(180deg)" }} />
          ) : (
            <GoArrowRight />
          )}
        </div>
      </Link>

      {/* Dots */}
      <div className="slider-dots">
        {slides.map((_, i) => (
          <span
            key={i}
            className={`dots ${activeSlide === i ? "active" : ""}`}
            onClick={() => scrollToIndex(i)}
          ></span>
        ))}
      </div>
    </div>
  );
};

export default VideoSlider;
